import React, { useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { WidgetGrid} from "../../components/widgetParts/WidgetGrid";
import { Widget, WidgetCache } from "system/widget/Widget";
import { Box, Alert, Button, Paper, Typography } from '@mui/material';
import "../../staticStyles/reactGrid.css";
import { useParams, Outlet } from "react-router-dom";
import { useDashboard } from "./Dashboard";
import APIRequest, { APIError, apiRequestCacheExpiration } from "../../system/APIRequest";
import { currentUser, InfegyAccountStub, InfegyUserStub } from "system/InfegyUser";
import DashboardTopbar from "components/dashboardParts/DashboardTopbar";
import { APIErrorAlert } from "components/apiHandling/APIErrorAlert";
import { useSnackbar } from "notistack";
import TemplateQuerySelector from "components/dashboardParts/TemplateQuerySelector";
import DashboardPageChangeArrows from "components/dashboardParts/DashboardPageChangeArrows";
import { BoxLoader } from "components/apiHandling/BoxLoader";
import { useTemplateQueries } from "./DashboardPageRoot";
import DashboardEditDialog from "components/widgetParts/DashboardEditDialog";
import { setDocumentTitle } from "system/Utilities";
import { useWidgetInteraction } from "./DashboardRoot";

export interface DashboardPageInterface {
  id: string;
  uuid: string;
  user_id: string;
  name: string;
  _deleted: boolean;
  created_at: string;
  updated_at: string;
  template_type: string | null;

  _deletable?: boolean;
  _modifiable?: boolean;
  _owned_by_you?: boolean;

  _user?: InfegyUserStub;
  _account?: InfegyAccountStub;

  // JSON dashboard properties.
  data: { layouts?: { [key: string]: any[] } };
}

export class DashboardPage implements DashboardPageInterface {
  id: string = "";
  uuid: string = "";
  user_id: string = "";
  name: string = "";
  _deleted: boolean = false;
  created_at: string = "";
  updated_at: string = "";
  template_type: string | null = null;
  data: { layouts?: { [key: string]: any[] } } = {};

  _deletable?: boolean = true;
  _modifiable?: boolean = true;
  _owned_by_you?: boolean = true;

  _user?: InfegyUserStub = { id: "", name: "", email: "", active: false };
  _account?: InfegyAccountStub = { id: "", name: "", active: false };

  setIsInLayoutMode:( isInLayoutMode:boolean ) => void = () => {};

  constructor(pageResponse: object) {
    const page : any = {...pageResponse};
    if (!page.data) {
      page.data = {layouts: {}, widgets: []};
    }
    if (!page.data?.layouts) {
      page.data.layouts = {};
    }

    Object.assign(this, page);
  }
}

const defaultLayouts = {
  lg: { x: 0, y: 9999, w: 3, h: 2 },
  xs: { x: 0, y: 9999, w: 1, h: 2 }
};

let autoRefreshTimeout: number | undefined = undefined;
let lastAutoRefreshInterval: number | undefined = undefined;

export default function DashboardPageContainer(): JSX.Element {
  const theme = useTheme();
  const widgetInteractionInfo = useWidgetInteraction();

  const exportRef = useRef();
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { dashboardId, dashboardPageId } = useParams();
  const dashboard = useDashboard();
  const templateQueriesContext = useTemplateQueries();

  const [isInLayoutMode, setIsInLayoutMode] = useState<boolean>(false);
  const [pageError, setPageError] = useState<APIError | null>(null);
  const [saveWidgetError, setSaveWidgetError] = useState<APIError | null>(null);
  const [savePageError, setSavePageError] = useState<APIError | null>(null);

  const [activePage, setActivePage] = useState<DashboardPage | null>(null);
  const [pageRefreshCount, setPageRefreshCount] = useState<number>(0);

  const [pageWidgets, setPageWidgets] = useState<Array<Widget>>([]);
  const [pageWidgetsLoaded, setPageWidgetsLoaded] = useState<boolean>(false);

  const [editDashboardDialogIsOpen, setEditDashboardDialogIsOpen] = useState<boolean>(false);

  const default_dashboard = currentUser()?.preferences?.default_dashboard;
  const isHomePage:boolean = default_dashboard === dashboardId;

  const [topbarIsPinned, setTopbarIsPinned] = React.useState<boolean>(localStorage.getItem('topbarIsPinned') === 'true');

  useEffect(() => {
    if (dashboard?.name) {
      setDocumentTitle(dashboard.name);
    } else {
      setDocumentTitle(i18n.t("nav.dashboard"));
    }
  }, []);

  // Load the dashboard page based on the ID in the URL
  useEffect(() => {
    if (activePage && activePage.id !== dashboardPageId) {
      setActivePage(null);
      setPageWidgets([]);
      setIsInLayoutMode(false);
    }

    if (dashboardPageId) {
      setPageError(null);
      APIRequest(`obj/dashboard-page/${dashboardPageId}`)
        .then((response: any) => {
          setActivePage(new DashboardPage(response?.output));
        }).catch((reason: APIError) => {
          setPageError(reason);
        });
    }
  }, [dashboardPageId]);

  const loadPageWidgets = () => {
    APIRequest(`obj/dashboard-page/${dashboardPageId}/entry`)
      .then((response: any) => {
        if (!Array.isArray(response?.output)) {
          response.output = response?.output ? [response?.output] : [];
        }
        const widgets = response?.output.map((widget: any) => {
          if(!widget.cache || widget.cache === null) {
            // When loading widgets from the page, if the cache is null, default it to an empty object
            // This is because the cache explicitly being set to null forces API reloads, avoiding request caching
            // On initial load, we want the cache! So ensure we default cache to empty object, not null
            widget.cache = {};
          }
          return new Widget(widget);
        });
        setPageWidgets(widgets);
        setPageWidgetsLoaded(true);
      }).catch((reason: APIError) => {
        setPageError(reason);
      });
  }

  // Load the page's widgets - they are stored as child objects of the page object in the API
  useEffect(() => {
    if (pageWidgets.length > 0 && !activePage || activePage?.id !== dashboardPageId) {
      setPageWidgets([]);
    }
    setIsInLayoutMode(false);
    setPageWidgetsLoaded(false);
    loadPageWidgets();
  }, [dashboardPageId]);

  // If the dashboard should autoupdate, set an interval to refresh the widget list to force a re-render
  useEffect(() => {
    const newAutoRefreshInterval = dashboard?.data?.autoRefreshInterval || 0;
    // If this isn't a fixed dashboard, we don't have a refresh interval, or the refresh interval is less than 30 seconds, don't set an interval
    if (!newAutoRefreshInterval || newAutoRefreshInterval < 30 || dashboard?.template_type) {
      if (autoRefreshTimeout) {
        window.clearInterval(autoRefreshTimeout);
        autoRefreshTimeout = undefined;
      }
      return;
    }

    if (!dashboard?.template_type && newAutoRefreshInterval > 30 && (!lastAutoRefreshInterval || newAutoRefreshInterval !== lastAutoRefreshInterval)) {
      lastAutoRefreshInterval = newAutoRefreshInterval;
      if (autoRefreshTimeout) {
        window.clearInterval(autoRefreshTimeout);
        autoRefreshTimeout = undefined;
      }
      autoRefreshTimeout = window.setInterval(() => {
        enqueueSnackbar(i18n.t("dashboard.auto-refreshing"), { variant: "info", autoHideDuration: 5000 });
        loadPageWidgets();
      }, newAutoRefreshInterval * 1000);
    } else {
      window.clearInterval(autoRefreshTimeout);
      autoRefreshTimeout = undefined;
    }
    return () => {
      if (autoRefreshTimeout) {
        window.clearInterval(autoRefreshTimeout);
        autoRefreshTimeout = undefined;
      }
    };
  }, [dashboard?.data?.autoRefreshInterval]);

  const pageTitle = dashboard.name;
  const pageSubTitle = activePage?.name;

  const handleUpdatePageName = (newPageName: string) => {
    setActivePage((prevActivePage: DashboardPage | null) => {
      if (prevActivePage === null) return null;
      return {...prevActivePage, name: newPageName};
    });
  };

  function savePageLayouts(newLayouts:object, resetPage:boolean = false) {
    if (!activePage || !dashboard._modifiable) {
      return;
    }

    const newLayoutsJSON = JSON.stringify(newLayouts),
          exisitingLayoutsJSON = JSON.stringify(activePage.data.layouts);

    if (newLayoutsJSON === exisitingLayoutsJSON) {
      // Nothing has changed, so don't save
      return;
    }

    if (savePageError) {
      setSavePageError(null);
    }

    activePage.data.layouts = newLayouts;

    // we used to store widgets in the page, this will remove them eventually
    // uncomment once prod and staging have these changes
    // delete activePage.data.widgets;

    const newPageData = JSON.stringify({data: activePage.data});

    if (!dashboard._modifiable) {
      return;
    }

    APIRequest(`obj/dashboard-page/${activePage.id}`, {}, {
      method: 'PUT',
      "body": newPageData
    }).then(() => {
      if (resetPage) {
        // This will cause the page to reload - this is the simplest way to accomplish this, open to other ideas!
        setPageRefreshCount(pageRefreshCount + 1);
      } else {
        setActivePage(new DashboardPage({...activePage}));
      }
    }).catch((reason: APIError) => {
      enqueueSnackbar(`${i18n.t("api-request.dashboard-save-error")}\n${reason.server_message}`, { variant: "error", persist: true });
    })
  }

  // Reload the widget's data from the server
  function reloadWidgetData(widgetId:string, isNew:boolean, reloadPageWidgets:boolean):void {
    APIRequest(`obj/dashboard-widget/${widgetId}`)
      .then((response: any) => {
        // Make sure the API response data is parsed by the Widget class so that we have data in the 
        // right format to work with.  For example, the API will return a string `widget.cache` but
        // we expect it to be parsed into an object that we can work with further down the line.
        const widget:Widget = new Widget(response?.output);
        const isExistingWidget = pageWidgets.find((pageWidget:Widget) => pageWidget.id === widgetId) !== undefined;
        let newWidgets = pageWidgets;
        if (isExistingWidget) {
          const widgetIndex = newWidgets.findIndex((pageWidget:Widget) => pageWidget.id === widgetId);
          if (widgetIndex !== -1) {
            newWidgets[widgetIndex] = widget;
          }
        } else {
          // This is an unrecognized widget, add it
          newWidgets = [...pageWidgets, widget];
          if (isNew) {
            // Add to the layouts
            const newLayouts = activePage?.data?.layouts || {};
            const keys = Object.keys(newLayouts);
            keys.forEach((key:string) => {
              if (!newLayouts[key]) {
                newLayouts[key] = [];
              }
              const newLayout = {...defaultLayouts[key]};
              newLayout.i = widgetId;
              newLayouts[key] = newLayouts[key].concat(newLayout);
            });
            savePageLayouts(newLayouts, true);
          }
        }
        if (reloadPageWidgets) {
          newWidgets = [...newWidgets];
        }
        if(isNew) {
          // If the widget is new, it won't exist yet on the page, so we have nothing to check
          setPageWidgets(newWidgets);
        } else {
          // If the widget is not new, it should exist on this page
          // We will check before updating state, as the page may have been changed while the load was in-flight
          setPageWidgets((previousPageWidgets:Widget[]) => {
            if(previousPageWidgets.find((pageWidget:Widget) => pageWidget.id === widgetId)) {
              return newWidgets;
            } else {
              return previousPageWidgets;
            }
          })
        }
      }).catch((reason: APIError) => {
        setSaveWidgetError(reason);
      });
  }

  // Add a new widget to the page given a type identifier string
  async function onAddWidget(type:string, widgetOptions?:any, startingLayouts?:any, openSettingsOnAdd?: boolean):Promise<void> {
    if (!dashboard._modifiable || !type) {
      return;
    }

    widgetOptions = widgetOptions || {};
    const body = {
      ...widgetOptions,
      uuid: self.crypto.randomUUID(),
      name: type,
      dashboard_page_id: activePage?.id
    };

    // First create a new widget of the given type using the API and get the key
    await APIRequest(`obj/dashboard-widget`, {}, {
      method: 'POST',
      "body": JSON.stringify(body)
      }).then((response: any) => {
        const newWidgetId = response?.id;
        if (!startingLayouts) {
          startingLayouts = {
            lg: {...defaultLayouts.lg},
            xs: {...defaultLayouts.xs}
          };
        }
        reloadWidgetData(newWidgetId, true, false);
        if (openSettingsOnAdd) {
          widgetInteractionInfo?.setNewWidgetID?.(newWidgetId);
        }
        
        // Delay a short bit to ensure the new widget is loaded, then scroll to it by ID
        setTimeout(() => {
          document.getElementById(body.uuid)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 250);
    }).catch((reason: APIError) => {
      setSaveWidgetError(reason);
    });
  }

  // Copy a widget to the page given an existing widget object
  function onCopyWidget(widget:Widget):void {
    const newWidget = {
      data: widget.data,
      request_info: widget.request_info,
      name: widget.name
    };

    // find the widget in current layouts and copy the existing layout info
    const oldWidgetId = widget.uuid,
          newLayouts = {},
          layoutKeys = Object.keys(activePage?.data?.layouts);
    layoutKeys.forEach((key:string) => {
      const breakpointLayouts = activePage?.data?.layouts[key];
      // find the widget in the current layouts
      const oldLayout = breakpointLayouts?.find((layout:any) => {
        return layout.i === oldWidgetId;
      });

      if (!oldLayout) {
        return;
      }
      // copy the layout info to the new widget without the id
      const newLayout = {...oldLayout};
      delete newLayout.i;
      // add the new layout to the new layouts
      newLayouts[key] = newLayout;
    });

    enqueueSnackbar(i18n.t("widgets.widget-copied"), { variant: "success" });
    onAddWidget(widget.name, newWidget, newLayouts, true);
  }

  // Save updated widget data -
  // the updatedWidgetInfo object should contain what is sent to the server,
  // typically the changed properties of the widget contained within an object.
  //
  // This will return a promise if the widget can be updated
  function onUpdateWidget(widgetId:string, updatedWidgetInfo:any, resetPage:boolean = false):Promise<any> {
    if (!dashboard._modifiable) {
      return Promise.reject(new Error("Dashboard is not modifiable"));
    }
    if (!widgetId || !updatedWidgetInfo) {
      return Promise.reject(new Error("Nothing to update"));
    }

    // Ensure we do not send an empty object or other cache data not serialized to a string. The API would get mad.
    let widgetInfo = updatedWidgetInfo;
    if (updatedWidgetInfo.cache && typeof(updatedWidgetInfo.cache) !== "string") {
      widgetInfo = {
        ...updatedWidgetInfo,
        cache: JSON.stringify(updatedWidgetInfo.cache)
      };
    }

    if (updatedWidgetInfo) {
      return APIRequest(`obj/dashboard-widget/${widgetId}`, {}, {
        method: 'PUT',
        body: JSON.stringify(widgetInfo)
      }, true).then(() => {
        setPageRefreshCount(pageRefreshCount + 1);
        const pageWidgetsCopy = pageWidgets.map((widget:Widget) => {
          if (widget.id === widgetId) {
            return Object.assign(new Widget({}), widget, widgetInfo);
          }
          return widget;
        });
        setPageWidgets(pageWidgetsCopy);
      }).catch((reason: APIError) => {
        enqueueSnackbar(`${i18n.t("error")}: ${reason.message}`, { variant: "error", persist: true });
      });
    }
    return Promise.resolve();
  }

  async function onUpdateWidgetData(widgetId:string, updatedWidgetData:any, resetPage:boolean = true):Promise<any>{
    if (!widgetId || !updatedWidgetData || !dashboard._modifiable) {
      console.error(`Missing widget ID or updated widget data: ${widgetId} ${updatedWidgetData}`);
      Sentry.captureMessage(`Missing widget ID or updated widget data: ${widgetId} ${updatedWidgetData}`);
      return Promise.reject("Nothing to update");
    }

    if (!dashboard._modifiable) {
      return Promise.reject("Dashboard is not modifiable");
    }

    try {
      await APIRequest(`obj/dashboard-widget/${widgetId}`, {}, {
        method: 'PUT',
        body: JSON.stringify({ data: updatedWidgetData })
      });
      if (resetPage) {
        setPageRefreshCount(pageRefreshCount + 1);
      }
      reloadWidgetData(widgetId, false, resetPage);
    } catch (reason_1) {
      enqueueSnackbar(`${i18n.t("error")}: ${reason_1?.message}`, { variant: "error", persist: true });
    }
  }

  // Delete a widget from the page given an existing widget placement object
  function onDeleteWidget(widgetId:string) {
    if (!activePage?.data.layouts) {
      return;
    }

    // get widget by id
    const widget = pageWidgets.find((widget:Widget) => {
      return widget.id === widgetId;
    });
    if (widget && widget?._deletable === false) {
      enqueueSnackbar(i18n.t("account-status.insufficient-account-permissions"), { variant: "error"});
      return;
    }

    const layouts:any = activePage?.data?.layouts;

    // remove the widget from the page layouts by id
    Object.keys(layouts).forEach((key:string) => {
      layouts[key] = layouts[key].filter((layout:any) => {
        return layout.i !== widgetId;
      });
    });

    // Delete the DB entry for the widget
    APIRequest(`obj/dashboard-widget/${widgetId}`, {}, {
      method: 'DELETE',
    }).then(() => {
      // update the page with the new widget list and layouts
      const newWidgets = pageWidgets.filter((widget:Widget) => {
        return widget.id !== widgetId;
      });
      setPageWidgets(newWidgets);
      savePageLayouts(layouts, true);
      return;
    }).catch((reason: APIError) => {
      enqueueSnackbar(reason.message, { variant: "error", persist: true });
      return reason;
    });
  }

  function onClearWidgets():void {
    // delete all widgets from the page
    if (!activePage?.data.layouts) {
      return;
    }
    // Async function to delete all widgets
    const deleteWidgets = async () => {
      // This filter catches an unusual case where the user doesn't have permission to delete a widget,
      // while having edit permissions on the page. This shouldn't happen, but there was an API bug that
      // caused this to happen in some limited cases. Now fixed, but some dashboards still have the issue
      // and we need to cover the possibility for potential future cases.
      const promises = pageWidgets.filter((widget:Widget) => {
        if (widget._deletable === false) {
          enqueueSnackbar(i18n.t("account-status.insufficient-permissions"), { variant: "error"});
          return;
        }
        return true;
    }).map((widget:Widget) => {
        return APIRequest(`obj/dashboard-widget/${widget.id}`, {}, {
          method: 'DELETE',
        });
      });
      await Promise.all(promises);
    };

    deleteWidgets().then(() => {
      // update the page with the new widget list and layouts
      setPageWidgets([]);
      savePageLayouts({}, true);
    }).catch((reason: APIError) => {
      setSaveWidgetError(reason);
    });
  }

  function onRefreshWidgets():void {
    // Clear the cache of all current widgets
    const refreshedWidgets = pageWidgets.map((widget:Widget) => {
      const refreshedWidget = {...widget};
      refreshedWidget.cache = {} as WidgetCache;
      return Object.assign(new Widget({}), refreshedWidget);
    });
    setPageWidgets(refreshedWidgets);
    enqueueSnackbar({message: i18n.t("dashboard.reload-widget-success", {minutes: Math.ceil(apiRequestCacheExpiration/(60*1000))}), variant: "info"});
  }

  const needsTemplateQuery = useMemo(() => {
    const templateQueries = templateQueriesContext?.allTemplateQueries;
    return dashboard?.template_type && (!templateQueries || !templateQueries.length);
  }, [templateQueriesContext?.allTemplateQueries, dashboard?.template_type]);

  const pinnedTopbarStyle = topbarIsPinned ? { overflowAnchor: 'none', position: 'sticky', top: "55px", zIndex: 1200 } : {};

  const dashboardPageAlertSx = { m: 0, mt: 2, mb: 2, p: 2 };

  return (
    <>
      <Box sx={{marginTop:"85px", pl:{xs:1, md:3}, pr:{xs:1, md:3}, mb: 0, pt: 0, pb: 0, ...pinnedTopbarStyle}}>
        <DashboardTopbar
          isOpen = {!isHomePage}
          currentPageId = {dashboardPageId || ""}
          isInLayoutMode = {isInLayoutMode}
          setIsInLayoutMode = {setIsInLayoutMode}
          onAddWidget = {onAddWidget}
          onClearWidgets = {onClearWidgets}
          onRefreshWidgets = {onRefreshWidgets}
          exportRef = {exportRef}
          widgets = {pageWidgets}
          handleUpdatePageName = {handleUpdatePageName}
          onUpdateWidget={onUpdateWidget}
          />
        { dashboard.template_type &&
          <TemplateQuerySelector
            showHighlights={!!needsTemplateQuery}
            isPinned={topbarIsPinned}
            setIsPinned={(value) => {
              setTopbarIsPinned(value);
              localStorage.setItem('topbarIsPinned', value.toString());
            }}
            onUpdateWidget={onUpdateWidget}
            onRefreshWidgets={() => {
              onRefreshWidgets()
              enqueueSnackbar(i18n.t("dashboard.widgets-refreshed"), { variant: "success" });
            }}
            pageWidgets={pageWidgets}
            />
        }
        {/* If this isn't a template dashboard, and we have a template query, it must be a default dashboard query.
          it's stored as the first template query and should be treated as a root dashboard query */}
        { !dashboard.template_type && templateQueriesContext.allTemplateQueries.length > 0 && !dashboard._is_public &&
          <Paper sx={{ p: 1, m: 0, mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h4" sx={{ mr: 1, fontWeight:'normal', color: theme.colors.secondary.main }}>
                {i18n.t("dashboard.root-dashboard-query")} :
                {!dashboard._modifiable
                  ?
                  <Typography sx={{ display: "inline-block", marginLeft: 1, fontWeight: "bold" }}>
                    {templateQueriesContext.allTemplateQueries[0].query.name}
                  </Typography>
                  :
                  <Box
                    sx={{ display: "inline-block", fontWeight: "bold", color: theme.colors.primary.main, pl: 1, cursor: dashboard._is_public ? "default" : "pointer" }}
                    onClick={() => {
                      if (!dashboard._is_public) {
                        setEditDashboardDialogIsOpen(true);
                      }
                    }}>
                    {templateQueriesContext.allTemplateQueries[0].query.name}
                  </Box>
                }
              </Typography>
            </Box>
          </Paper>
        }
      </Box>
      <Box sx={{mt: 0, pl:{xs:1, md:3}, pr:{xs:1, md:3}, mb: 0, pt: 0, pb: 0, overflowAnchor: 'none', position: 'sticky', top: "55px", zIndex: 1201, displayPrint: 'none'}}>
        {isInLayoutMode &&
          <Box sx={{ backgroundColor: theme.palette.background.default }}>
          <Alert
            variant="outlined"
            severity="success"
            sx={dashboardPageAlertSx}
            action={
              <Button
                color="info"
                variant="contained"
                size="small"
                onClick={() => setIsInLayoutMode(false)} >
                {i18n.t("dashboard.view-mode")}
              </Button>
            }
            >
            {i18n.t("dashboard.layout-mode-alert")}
          </Alert>
          </Box>
        }
      </Box>
      <Box sx={{ mt: 0, pl:{xs:1, md:3}, pr:{xs:1, md:3}, mb: 0, pt: 0, pb: 0, displayPrint: 'none'}}>
        { needsTemplateQuery &&
          <Alert variant="outlined" severity="warning" sx={dashboardPageAlertSx} >
            {i18n.t("dashboard.template-missing-query")}
          </Alert>
        }
        { pageWidgetsLoaded && pageWidgets?.length === 0 &&
          <Alert variant="outlined" severity="info" sx={dashboardPageAlertSx} >
            {i18n.t("dashboard.empty-page-alert")}
          </Alert>
        }
        {pageError &&
          <APIErrorAlert fetchError={pageError}>Load Page</APIErrorAlert>
        }
        {savePageError &&
          <APIErrorAlert fetchError={savePageError}>Save Page</APIErrorAlert>
        }
        {saveWidgetError &&
          <APIErrorAlert fetchError={saveWidgetError}>Save Widget</APIErrorAlert>
        }
      </Box>
      <Box ref={exportRef} sx={{m: 0, pt:2, pb: 0.5, pl:{xs:1, md:3}, pr:{xs:1, md:3}}}>
        <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end", p: 0, pt: 0, pb: 2, }}>
          <Typography variant="h3" sx={{ opacity: "0.7", textAlign: "left", fontWeight: "600" }} >
            {pageTitle}
          </Typography>
          <Typography variant="h2" sx={{ fontSize: "1.2rem", opacity: "0.7", fontWeight: "normal", textAlign: "right", verticalAlign: "bottom", paddingBottom: "3px" }} >
            {pageSubTitle}
          </Typography>
        </Box>
        {!pageWidgetsLoaded && !pageError &&
          <BoxLoader></BoxLoader>
        }
        <Box
          sx={{
            bgcolor: theme.palette.background.default,
            marginBottom: isInLayoutMode ? '250px' : '50px',
            ml:0,
            mr:0
          }}>
          {activePage?.data?.layouts && !!pageWidgets?.length && pageWidgetsLoaded &&
            <WidgetGrid
              widgetList = {pageWidgets}
              layouts = {activePage?.data?.layouts as Layouts}
              savePageLayouts = {savePageLayouts}
              isInLayoutMode = {isInLayoutMode}
              setIsInLayoutMode = {setIsInLayoutMode}
              onDeleteWidget = {onDeleteWidget}
              onCopyWidget = {onCopyWidget}
              onUpdateWidget = {onUpdateWidget}
              onUpdateWidgetData = {onUpdateWidgetData}
              >
            </WidgetGrid>}
        </Box>
      </Box>
      <Box>
        <Outlet />
        {dashboardPageId &&
          <DashboardPageChangeArrows
            currentPageId={dashboardPageId}
            handleUpdatePageName={handleUpdatePageName}
          />
        }
        {editDashboardDialogIsOpen &&
          <DashboardEditDialog
            setDialogIsOpen = {setEditDashboardDialogIsOpen}
            dashboardId = {dashboard.id}
            onClose = {()=>{
              setEditDashboardDialogIsOpen(false);
              window.location.reload();
            }}
            />
        }
      </Box>
    </>
  );
}
